<template>
  <div class="budget-alocacao">
    <div class="d-flex align-items-center mb-1">
      <h5 class="mb-0">Alocação de Budget</h5>
      <template v-if="budget">
        <b-badge :variant="(budget.categoria || 'ELT') === 'ELT' ? 'light-primary' : 'light-info'" class="ml-1">
          {{ budget.categoria || 'ELT' }}
        </b-badge>
        <small class="text-muted ml-50">
          {{ (budget.categoria || 'ELT') === 'ELT' ? 'English Language Teaching' : 'Global Languages' }}
        </small>
      </template>
    </div>
    <p class="text-muted small mb-1">
      Distribua a verba do budget vigente entre os seus representantes e faça
      transferências entre eles. O valor total e o período do budget são definidos
      pela Diretoria — aqui você apenas divide e remaneja a verba.
    </p>

    <div v-if="isLoading && !budget" class="text-center py-5"><b-spinner /></div>

    <b-card v-else-if="!budgetsVigentes.length" class="text-center">
      <div class="text-muted py-4">
        Nenhum budget vigente disponível para alocação no momento.
      </div>
    </b-card>

    <template v-else>
      <b-card v-if="budgetsVigentes.length > 1" title="Budget vigente">
        <b-form-group label="Selecione o budget para alocar">
          <b-form-select v-model="budgetIdSelecionado" :options="budgetOptions" @change="onTrocarBudget" />
        </b-form-group>
      </b-card>

      <template v-if="budget">
        <b-row>
          <b-col md="3">
            <b-card class="text-center">
              <small class="text-muted d-block">Valor Total</small>
              <h4 class="mb-0">{{ formatarMoeda(budget.valor) }}</h4>
            </b-card>
          </b-col>
          <b-col md="3">
            <b-card class="text-center">
              <small class="text-muted d-block">Alocado</small>
              <h4 class="mb-0 text-info">{{ formatarMoeda(somaAlocado) }}</h4>
            </b-card>
          </b-col>
          <b-col md="3">
            <b-card class="text-center">
              <small class="text-muted d-block">Não alocado</small>
              <h4 class="mb-0" :class="naoAlocado < 0 ? 'text-danger' : 'text-success'">
                {{ formatarMoeda(naoAlocado) }}
              </h4>
            </b-card>
          </b-col>
          <b-col md="3">
            <b-card class="text-center">
              <small class="text-muted d-block">Gasto</small>
              <h4 class="mb-0 text-warning">{{ formatarMoeda(budget.valorgasto) }}</h4>
            </b-card>
          </b-col>
        </b-row>

        <b-card title="Distribuição por representante">
          <p class="text-muted small mb-1">
            Defina quanto cada representante pode utilizar. A soma das alocações não
            pode ultrapassar o valor total, e nenhuma alocação pode ficar abaixo do
            valor já gasto pelo representante.
          </p>
          <div v-if="loadingReps" class="text-center py-3"><b-spinner small /></div>
          <template v-else>
            <b-table
              :items="linhasAlocacao"
              :fields="alocFields"
              small
              responsive
              class="mb-1"
            >
              <template #cell(representante_nome)="data">
                {{ data.item.representante_nome || data.item.representanteid }}
                <b-badge v-if="data.item.foraCarteira" variant="light-warning" class="ml-25">
                  fora da carteira
                </b-badge>
              </template>
              <template #cell(valorgasto)="data">{{ formatarMoeda(data.item.valorgasto) }}</template>
              <template #cell(valor)="data">
                <b-form-input
                  v-model="alocacaoMap[data.item.representanteid]"
                  type="text"
                  inputmode="decimal"
                  size="sm"
                  style="max-width: 160px"
                  @input="mascararCampoAlocacao(data.item.representanteid)"
                />
              </template>
              <template #cell(saldo)="data">
                <span :class="saldoLinha(data.item) < 0 ? 'text-danger' : 'text-success'">
                  {{ formatarMoeda(saldoLinha(data.item)) }}
                </span>
              </template>
            </b-table>
            <div class="d-flex justify-content-between align-items-center">
              <small :class="naoAlocado < 0 ? 'text-danger' : 'text-muted'">
                Soma das alocações: {{ formatarMoeda(somaEditada) }} de {{ formatarMoeda(budget.valor) }}
              </small>
              <b-button
                variant="relief-primary"
                :disabled="isSaving || naoAlocadoEditado < 0"
                @click="salvar"
              >
                <b-spinner v-if="isSaving" small class="mr-25" />
                Salvar alocações
              </b-button>
            </div>
          </template>
        </b-card>

        <b-card title="Transferência entre representantes">
          <p class="text-muted small mb-1">
            Move verba de um representante (ou da reserva não alocada) para outro. A
            transferência não altera o valor total do budget. Origem e destino devem
            ser diferentes.
          </p>
          <b-form @submit.prevent="aplicarTransferencia">
            <b-row class="align-items-end">
              <b-col md="3" sm="6">
                <b-form-group label="Origem *">
                  <b-form-select v-model="transfer.origemRepresentanteid" :options="destinoOptions" />
                </b-form-group>
              </b-col>
              <b-col md="3" sm="6">
                <b-form-group label="Destino *">
                  <b-form-select v-model="transfer.destinoRepresentanteid" :options="destinoOptions" />
                </b-form-group>
              </b-col>
              <b-col md="2" sm="6">
                <b-form-group label="Valor *">
                  <b-form-input
                    v-model="transfer.valor"
                    type="text"
                    inputmode="decimal"
                    placeholder="Ex.: 500,00"
                    @input="transfer.valor = mascaraMoedaBR(transfer.valor)"
                  />
                </b-form-group>
              </b-col>
              <b-col md="2" sm="12">
                <b-form-group label="Motivo *">
                  <b-form-input
                    v-model="transfer.motivo"
                    maxlength="255"
                    placeholder="Justificativa"
                  />
                </b-form-group>
              </b-col>
              <b-col md="2" sm="6">
                <b-form-group label=" ">
                  <b-button block variant="relief-info" type="submit" :disabled="isSaving">
                    <b-spinner v-if="isSaving" small class="mr-25" />
                    Transferir
                  </b-button>
                </b-form-group>
              </b-col>
            </b-row>
          </b-form>
        </b-card>

        <b-card title="Histórico de movimentações">
          <div v-if="!budget.logs || !budget.logs.length" class="text-muted">Nenhuma movimentação registrada.</div>
          <b-table
            v-else
            :items="budget.logs"
            :fields="logFields"
            small
            responsive
            class="mb-0"
          >
            <template #cell(datahora)="data">{{ formatarDataHora(data.item.datahora) }}</template>
            <template #cell(representante_nome)="data">{{ data.item.representante_nome || '-' }}</template>
            <template #cell(valor)="data">{{ formatarMoeda(data.item.valor) }}</template>
          </b-table>
        </b-card>
      </template>
    </template>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BButton, BSpinner, BTable,
  BForm, BFormGroup, BFormInput, BFormSelect,
} from 'bootstrap-vue';
import { mapActions, mapState } from 'vuex';
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { parseDecimalBR, formatDecimalBR, mascaraMoedaBR } from '@/libs/numero';

export default {
  name: 'AlocacaoBudget',
  components: {
    BCard, BRow, BCol, BButton, BSpinner, BTable,
    BForm, BFormGroup, BFormInput, BFormSelect,
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      budgetsVigentes: [],
      budgetIdSelecionado: null,
      loadingReps: false,
      representantesSup: [],
      alocacaoMap: {},
      transfer: {
        origemRepresentanteid: null, destinoRepresentanteid: null, valor: '0,00', motivo: '',
      },
      alocFields: [
        { key: 'representante_nome', label: 'Representante' },
        { key: 'valorgasto', label: 'Gasto', class: 'text-right' },
        { key: 'valor', label: 'Alocado', class: 'text-right' },
        { key: 'saldo', label: 'Saldo', class: 'text-right' },
      ],
      logFields: [
        { key: 'datahora', label: 'Data/Hora' },
        { key: 'descricao', label: 'Descrição' },
        { key: 'representante_nome', label: 'Representante' },
        { key: 'valor', label: 'Valor', class: 'text-right' },
      ],
    };
  },
  computed: {
    ...mapState('Budget', { budget: 'budgetAtual', isLoading: 'isLoading', isSaving: 'isSaving' }),
    budgetOptions() {
      // Com delegação ativa o supervisor pode ver budgets de outro supervisor:
      // nesse caso, identifica o dono de cada budget na opção.
      const multiSup = new Set(this.budgetsVigentes.map(b => String(b.supervisorid))).size > 1;
      return this.budgetsVigentes.map(b => ({
        value: b.budgetid,
        text: `[${b.categoria || 'ELT'}] ${b.descricao || 'Budget'} — ${this.formatarMoeda(b.valor)} (${this.formatarData(b.datainicio)} a ${this.formatarData(b.datatermino)})${multiSup && b.supervisor_nome ? ` — ${b.supervisor_nome}` : ''}`,
      }));
    },
    // Linhas da tabela: um registro por representante do supervisor, com o gasto
    // atual (se já houver alocação). Mantém ordem por nome.
    linhasAlocacao() {
      const gastoPorRep = new Map(
        ((this.budget && this.budget.alocacoes) || []).map(a => [String(a.representanteid), a]),
      );
      const linhas = (this.representantesSup || []).map((r) => {
        const aloc = gastoPorRep.get(String(r.representanteid));
        return {
          representanteid: String(r.representanteid),
          representante_nome: r.representantenome || r.representante_nome || r.nome,
          valorgasto: aloc ? Number(aloc.valorgasto || 0) : 0,
          foraCarteira: false,
        };
      });
      // Reps com alocação no budget mas que saíram da carteira do supervisor:
      // continuam visíveis (e editáveis) para não sumirem/serem excluídos no save.
      const naCarteira = new Set(linhas.map(l => l.representanteid));
      gastoPorRep.forEach((aloc, id) => {
        if (!naCarteira.has(id)) {
          linhas.push({
            representanteid: id,
            representante_nome: aloc.representante_nome || aloc.representantenome || id,
            valorgasto: Number(aloc.valorgasto || 0),
            foraCarteira: true,
          });
        }
      });
      return linhas;
    },
    somaAlocado() {
      return ((this.budget && this.budget.alocacoes) || [])
        .reduce((acc, a) => acc + Number(a.valor || 0), 0);
    },
    naoAlocado() {
      return Number((this.budget && this.budget.valor) || 0) - this.somaAlocado;
    },
    somaEditada() {
      return Object.values(this.alocacaoMap)
        .reduce((acc, v) => acc + parseDecimalBR(v), 0);
    },
    naoAlocadoEditado() {
      return Number((this.budget && this.budget.valor) || 0) - this.somaEditada;
    },
    destinoOptions() {
      const opts = [{ value: null, text: 'Reserva não alocada (supervisor)' }];
      (this.representantesSup || []).forEach((r) => {
        opts.push({
          value: String(r.representanteid),
          text: `Representante: ${r.representantenome || r.representante_nome || r.nome || r.representanteid}`,
        });
      });
      return opts;
    },
  },
  async mounted() {
    await this.carregarVigentes();
  },
  methods: {
    formatDecimalBR,
    mascaraMoedaBR,
    // Máscara ao vivo (dígitos entram pelos centavos; sempre exibe "0,00"+).
    mascararCampoAlocacao(representanteid) {
      const atual = this.alocacaoMap[representanteid];
      const mascarado = mascaraMoedaBR(atual);
      if (mascarado !== atual) this.$set(this.alocacaoMap, representanteid, mascarado);
    },
    ...mapActions('Budget', ['listar', 'buscarPorId', 'transferir', 'salvarAlocacoes']),
    async carregarVigentes() {
      try {
        await this.$store.dispatch('Budget/setFilters', { vigentes: '1' });
        await this.listar();
        this.budgetsVigentes = (this.$store.state.Budget.budgets || []).filter(b => b.ativo);
        if (this.budgetsVigentes.length) {
          this.budgetIdSelecionado = this.budgetsVigentes[0].budgetid;
          await this.carregarBudget(this.budgetIdSelecionado);
        }
      } catch (e) {
        // erro já exibido pelo client
      }
    },
    async onTrocarBudget() {
      if (this.budgetIdSelecionado) {
        await this.carregarBudget(this.budgetIdSelecionado);
      }
    },
    async carregarBudget(id) {
      const budget = await this.buscarPorId(id);
      this.resetTransfer();
      await this.carregarRepsDoSupervisor(budget);
      this.sincronizarMapa(budget);
    },
    async carregarRepsDoSupervisor(budget) {
      this.loadingReps = true;
      try {
        const reps = await this.$store.dispatch('Budget/carregarRepresentantes', budget.supervisorid);
        this.representantesSup = reps || [];
      } catch (e) {
        this.representantesSup = [];
      } finally {
        this.loadingReps = false;
      }
    },
    sincronizarMapa(budget) {
      const map = {};
      const atual = new Map(
        ((budget && budget.alocacoes) || []).map(a => [String(a.representanteid), Number(a.valor || 0)]),
      );
      (this.representantesSup || []).forEach((r) => {
        const id = String(r.representanteid);
        map[id] = formatDecimalBR(atual.has(id) ? atual.get(id) : 0) || '0,00';
      });
      // Preserva alocações de reps fora da carteira atual do supervisor.
      atual.forEach((valor, id) => {
        if (!(id in map)) map[id] = formatDecimalBR(valor) || '0,00';
      });
      this.alocacaoMap = map;
    },
    saldoLinha(item) {
      const alocado = parseDecimalBR(this.alocacaoMap[item.representanteid]);
      return alocado - Number(item.valorgasto || 0);
    },
    async salvar() {
      if (this.naoAlocadoEditado < 0) {
        return this.aviso('A soma das alocações não pode ultrapassar o valor total do budget');
      }
      const alocacoes = [];
      let abaixoDoGasto = false;
      this.linhasAlocacao.forEach((linha) => {
        const valor = parseDecimalBR(this.alocacaoMap[linha.representanteid]);
        if (valor < Number(linha.valorgasto || 0)) abaixoDoGasto = true;
        if (valor > 0 || Number(linha.valorgasto || 0) > 0) {
          alocacoes.push({ representanteid: linha.representanteid, valor });
        }
      });
      if (abaixoDoGasto) {
        return this.aviso('Nenhuma alocação pode ficar abaixo do valor já gasto pelo representante');
      }
      try {
        await this.salvarAlocacoes({
          id: this.budgetIdSelecionado,
          payload: { alocacoes },
        });
        this.sincronizarMapa(this.budget);
        this.sucesso('Alocações salvas');
      } catch (e) {
        // erro já exibido pelo client
      }
      return null;
    },
    async aplicarTransferencia() {
      const valor = parseDecimalBR(this.transfer.valor);
      if (!valor || valor <= 0) {
        return this.aviso('Informe um valor de transferência maior que zero');
      }
      const origem = this.transfer.origemRepresentanteid || null;
      const destino = this.transfer.destinoRepresentanteid || null;
      if (origem === destino) {
        return this.aviso('Origem e destino devem ser diferentes');
      }
      if (!this.transfer.motivo || !this.transfer.motivo.trim()) {
        return this.aviso('Informe o motivo da transferência');
      }
      try {
        await this.transferir({
          id: this.budgetIdSelecionado,
          payload: {
            origemRepresentanteid: origem,
            destinoRepresentanteid: destino,
            valor,
            motivo: this.transfer.motivo.trim(),
          },
        });
        this.resetTransfer();
        this.sincronizarMapa(this.budget);
        this.sucesso('Transferência realizada');
      } catch (e) {
        // erro já exibido pelo client
      }
      return null;
    },
    resetTransfer() {
      this.transfer = {
        origemRepresentanteid: null, destinoRepresentanteid: null, valor: '0,00', motivo: '',
      };
    },
    aviso(title) {
      this.toast({ component: ToastificationContent, props: { title, icon: 'AlertTriangleIcon', variant: 'warning' } });
    },
    sucesso(title) {
      this.toast({ component: ToastificationContent, props: { title, icon: 'CheckCircleIcon', variant: 'success' } });
    },
    formatarMoeda(v) {
      return (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },
    formatarData(d) {
      if (!d) return '-';
      const dt = new Date(d);
      if (Number.isNaN(dt.getTime())) return d;
      return dt.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
    },
    formatarDataHora(d) {
      if (!d) return '-';
      const dt = new Date(d);
      if (Number.isNaN(dt.getTime())) return d;
      return dt.toLocaleString('pt-BR');
    },
  },
};
</script>
